import { Apis, logger } from '@/Util/globals';
import { MAGNOLIA_CHANNEL_PARAMETER, MAGNOLIA_PREVIEW_PARAMETER } from '@/Util/globalConstants';
import type { MagnoliaSite, SiteConfigParameters } from '@/types/cms/magnolia';
import type { IMagnoliaContext } from '@magnolia/template-annotations/src/service/GetMagnoliaContext';
import { EditorContextHelper } from '@magnolia/template-annotations';
import { apiFetch } from '@/Util/api';
import { fallbackLng, languages } from '@/app/i18n/settingsClient';
import { ReadonlyURLSearchParams } from 'next/navigation';

export const getRequestConfig404 = async (
  apis: Apis,
  searchParams: ReadonlyURLSearchParams,
  isAuthorString?: string
): Promise<{
  magnoliaContext: IMagnoliaContext;
  rootNodePath: string;
  locale: string;
  siteConfig: MagnoliaSite;
  isAuthor: boolean;
  resolvedUrl: string;
  fetchTTL?: string;
}> => {
  let resolvedUrl = window.location.href;
  const newResolvedUrl = new URL(resolvedUrl);
  let isAuthor: boolean = false;
  let isPreview: boolean;
  const siteRequestParams = new URLSearchParams();
  const mgnlPreview = searchParams.get('mgnlPreview');
  const mgnlChannel = searchParams.get('mgnlChannel');
  isPreview = mgnlPreview === 'true';
  const fetchTTL = process.env.FETCH_TTL;

  if (isAuthorString !== 'true' || !isPreview) {
    siteRequestParams.set('host', newResolvedUrl.hostname);
  } else if (isAuthorString === 'true' || isPreview) {
    isAuthor = true;
  }
  const siteApiUrl = `${apis.site}/resolve?${siteRequestParams.toString()}`;
  let siteConfig: MagnoliaSite = {
    name: '',
    enabled: false,
    i18n: {
      locale: fallbackLng,
      locales: [fallbackLng],
      defaultLocale: fallbackLng,
      enabled: false,
      fallbackLocale: fallbackLng
    },
    parameters: {} as SiteConfigParameters,
    mappings: {}
  };
  try {
    logger('debug')('Fetching site config', siteApiUrl);
    const siteRes = await apiFetch(siteApiUrl, undefined, isAuthor, fetchTTL);
    siteConfig = await siteRes.json();
  } catch (err) {
    logger('error')('Fetching site config failed:', err);
  }
  logger('debug')('Fetching site config finished. Tenant:', siteConfig.name);
  const locale = siteConfig.i18n.defaultLocale;
  const rootNodePath =
    Object.values(siteConfig.mappings).find(mapping => mapping.repository === 'website')
      ?.handlePrefix ?? '/';

  if (isPreview || mgnlChannel) {
    const newResolvedUrl = new URL(resolvedUrl || 'http://localhost');
    if (mgnlPreview) {
      newResolvedUrl.searchParams.append(MAGNOLIA_PREVIEW_PARAMETER, mgnlPreview);
    }
    if (mgnlChannel) {
      newResolvedUrl.searchParams.append(MAGNOLIA_CHANNEL_PARAMETER, mgnlChannel);
    }
    resolvedUrl = newResolvedUrl.toString();
  }
  const finalUrl = new URL(resolvedUrl || 'http://localhost');
  const pathname = finalUrl.pathname;
  const magnoliaContext: IMagnoliaContext = EditorContextHelper.getMagnoliaContext(
    `/${locale || fallbackLng}${pathname}${finalUrl.search}`,
    rootNodePath,
    languages
  );

  return {
    magnoliaContext,
    rootNodePath,
    locale: locale || fallbackLng,
    siteConfig,
    isAuthor,
    resolvedUrl,
    fetchTTL
  };
};
